import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import client from "@/lib/api";
import { flushAttribution, captureAttribution } from "@/lib/attribution";
import { resetVoiceConsentCache } from "@/components/VoiceConsentGate";
import { resetPinnedServicesCache } from "@/features/screening-room/components/FilterControls.jsx";
import { resetFilterSectionsCache } from "@/features/screening-room/components/FilterPanel.jsx";

const AuthContext = createContext(null);
const USER_CACHE_KEY = "worley_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  // Hybrid web auth (2026-10-23): the raw JWT is no longer kept in localStorage
  // for the web app — it lives ONLY in an httpOnly cookie now (see api.js /
  // SessionCookieMiddleware in server.py), unreadable by JS, closing the main
  // XSS token-theft vector. This flag is NOT a credential — it's just a UX hint
  // ("did login/signup succeed at some point") deciding whether to probe
  // /auth/me on load; worst case of it being wrong is one extra 401.
  const hasSession = localStorage.getItem("sc_has_session") === "1";
  const tzCheckedRef = useRef(false);
  // Clerk migration Phase 2: a Clerk session is a second, equally valid way
  // to be authenticated. `user` (and everything derived from it below) must
  // end up identical whichever path got the visitor signed in.
  const clerkAuth = useClerkAuth();
  // If Clerk's SDK is slow/unreachable (network block, ad-blocker, outage),
  // `clerkAuth.isLoaded` can stay false forever — nothing else would ever
  // flip it. Bound how long we wait on it so a legacy-only or anonymous
  // visitor is never stuck on a loading screen because of Clerk.
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  useEffect(() => {
    if (clerkAuth.isLoaded) return;
    const t = setTimeout(() => setClerkTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, [clerkAuth.isLoaded]);
  const clerkReady = clerkAuth.isLoaded || clerkTimedOut;
  const clerkSignedIn = clerkAuth.isLoaded && clerkAuth.isSignedIn; // never trust isSignedIn if Clerk never actually loaded

  const persist = (data) => {
    // The cookie itself is set server-side by SessionCookieMiddleware (it
    // mirrors this same response's `token` field) — nothing to store here.
    localStorage.setItem("sc_has_session", "1");
    localStorage.setItem("worley_refresh_at", String(Date.now()));
    try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
    resetPinnedServicesCache();
    resetFilterSectionsCache();
    setUser(data.user);
  };

  // Handle a mobile-app "continue on web" handoff (?handoff=<code>): exchanges
  // the single-use code for a real session without retyping credentials.
  // The code is stripped from the URL synchronously, BEFORE the async
  // consume call — React 18 StrictMode double-invokes effects in dev, and
  // without this a second invocation would see the same `code` still in the
  // URL and send a second (already-used, 400) consume request.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("handoff");
    if (!code) return;
    params.delete("handoff");
    const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
    window.history.replaceState(null, "", clean);
    client.post("/auth/handoff/consume", { code })
      .then((r) => { persist(r.data); flushAttribution(); window.location.replace(clean); })
      .catch(() => {});
  }, []);

  // Handle Emergent-managed Google OAuth redirect (#session_id=...)
  useEffect(() => {
    captureAttribution(); // first-touch share/platform attribution from URL
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sessionId = decodeURIComponent(m[1]);
    window.history.replaceState(null, "", window.location.pathname);
    client.post("/auth/google", { session_token: sessionId })
      .then((r) => {
        persist(r.data);
        flushAttribution();
        const hasGenres = (r.data.user?.favorite_genres || []).length > 0;
        window.location.replace(hasGenres ? "/app" : "/app/welcome");
      })
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    // Only wait on Clerk when a legacy session can't already answer the
    // question — a legacy session must never be delayed by Clerk's load
    // state, bounded or not.
    if (!hasSession && !clerkReady) return;
    if (!hasSession && !clerkSignedIn) {
      setUser(false);
      return;
    }
    // A Clerk sign-in can flip isSignedIn true while `user` is still `false`
    // from before — without this, guards would see the stale `false` and
    // bounce away before the /auth/me hydration below has a chance to land.
    setUser((prev) => (prev ? prev : null));
    let cancelled = false;
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(USER_CACHE_KEY) || "null"); } catch { /* ignore */ }
    const hydrate = async (attempt = 0) => {
      try {
        const r = await client.get("/auth/me");
        if (cancelled) return;
        try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(r.data)); } catch { /* ignore */ }
        setUser(r.data);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          // Session genuinely invalid/expired — this is the only real sign-out.
          localStorage.removeItem("sc_has_session");
          localStorage.removeItem(USER_CACHE_KEY);
          resetPinnedServicesCache();
          resetFilterSectionsCache();
          setUser(false);
          // Clerk can still report isSignedIn=true even though OUR backend
          // just rejected this exact session (e.g. an identity that couldn't
          // be resolved) — without this, /sign-in sees isSignedIn=true and
          // immediately bounces straight back to /app, which 401s again: an
          // infinite loop that renders as a blank/flickering screen (found
          // 2026-09-17). Scoped strictly to THIS 401 from THIS /auth/me call
          // — never on network errors/5xx/timeouts or any other endpoint's
          // 401 — a blanket rule would trade this bug for surprise logouts
          // during ordinary transient backend blips.
          if (clerkSignedIn) {
            try { await clerkAuth.signOut(); } catch { /* ignore */ }
          }
        } else if (status === 403 && e?.response?.data?.detail === "age_verification_required") {
          // 18+ Age Eligibility Gate: a brand-new Clerk identity (typically a
          // social sign-up/sign-in with no eligibility data attached) was
          // declined server-side — no local account was ever created, and
          // the Clerk-side identity was deleted too. Clear this dead session
          // and send them into the fully-gated /sign-up flow to try again
          // with the birth-date step included.
          localStorage.removeItem("sc_has_session");
          localStorage.removeItem(USER_CACHE_KEY);
          resetPinnedServicesCache();
          resetFilterSectionsCache();
          setUser(false);
          if (clerkSignedIn) {
            try { await clerkAuth.signOut(); } catch { /* ignore */ }
          }
          window.location.replace("/sign-up?age_gate_retry=1");
        } else if (attempt < 3) {
          // Transient failure (network blip, 429, 5xx, timeout, CDN challenge):
          // keep the session and retry with backoff — never bounce to /login.
          setTimeout(() => hydrate(attempt + 1), 800 * (attempt + 1));
        } else if (cached) {
          // Still failing but we know who they are — stay signed in optimistically;
          // per-page API calls will recover once the network settles.
          setUser(cached);
        } else {
          setUser(false);
        }
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, [hasSession, clerkReady, clerkSignedIn]);

  // Silent sliding-session refresh: while signed in, proactively swap the token
  // for a fresh one so an active session never expires mid-use. The backend
  // /auth/refresh only succeeds on a still-valid, non-revoked token, so this
  // slides the 7-day window forward without weakening revocation. The web
  // app never sees the new token value — SessionCookieMiddleware re-sets the
  // httpOnly cookie straight from this same response server-side.
  useEffect(() => {
    if (!user) return undefined;
    const REFRESH_MS = 6 * 60 * 60 * 1000; // check/refresh every 6h while open
    const MIN_GAP_MS = 60 * 60 * 1000;     // never more than hourly (multi-tab throttle)
    let stopped = false;
    const doRefresh = async () => {
      if (stopped || localStorage.getItem("sc_has_session") !== "1") return;
      const last = parseInt(localStorage.getItem("worley_refresh_at") || "0", 10);
      if (Date.now() - last < MIN_GAP_MS) return;
      try {
        await client.post("/auth/refresh");
        localStorage.setItem("worley_refresh_at", String(Date.now()));
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sc_has_session");
          localStorage.removeItem(USER_CACHE_KEY);
          resetPinnedServicesCache();
          resetFilterSectionsCache();
          setUser(false);
        }
        // transient errors: keep the session, try again next tick
      }
    };
    doRefresh();
    const iv = setInterval(doRefresh, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === "visible") doRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user]);

  // Auto-detect the browser's own timezone once per page load (not per navigation —
  // this component only mounts once at the app root). Silent, best-effort, and never
  // overwrites a timezone the user explicitly chose in Settings (server-enforced).
  useEffect(() => {
    if (!user || tzCheckedRef.current) return;
    tzCheckedRef.current = true;
    (async () => {
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!browserTz) return;
        const { data } = await client.get("/me/preferences");
        if (data.timezone !== browserTz) {
          await client.patch("/me/preferences", { timezone: browserTz, timezone_source: "auto" });
        }
      } catch { /* best-effort — never block the app on this */ }
    })();
  }, [user]);

  // Legacy web login/signup/MFA UI was retired (2026-xx-xx): Clerk's
  // /sign-in and /sign-up now cover every web visitor — new signups go
  // straight through Clerk, and an existing legacy-password account gets
  // auto-linked to its Clerk identity on first matching-email sign-in (see
  // get_or_create_user_for_clerk in clerk_auth.py), so no data is stranded.
  // The wrapper functions that only ever served the now-deleted Login.js /
  // Signup.js pages (login, register, verifyMfa, loginWithGoogle,
  // applySession) were removed along with those pages. The underlying
  // backend endpoints (/auth/login, /auth/signup, /auth/mfa-verify,
  // /auth/google) are untouched — the Expo mobile app authenticates against
  // them directly and has no Clerk integration.

  const changePassword = async (currentPassword, newPassword) => {
    const { data } = await client.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
    persist(data);
    return data.user;
  };

  const refresh = async () => {
    const { data } = await client.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Terminate whichever session(s) actually exist: revoke the httpOnly
    // cookie server-side (it's httpOnly, so JS can't clear it itself), a
    // real server-side revoke for Clerk (signOut()), then clear local state.
    try { await client.post("/auth/logout"); } catch { /* best-effort */ }
    localStorage.removeItem("sc_has_session");
    localStorage.removeItem(USER_CACHE_KEY);
    resetVoiceConsentCache();
    resetPinnedServicesCache();
    resetFilterSectionsCache();
    setUser(false);
    if (clerkAuth.isSignedIn) {
      try { await clerkAuth.signOut(); } catch { /* ignore — state is already cleared above */ }
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, changePassword, logout, refresh, authMethod: clerkSignedIn ? "clerk" : (hasSession ? "legacy" : null) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
